import FileUploadIcon from '@mui/icons-material/FileUpload';
import {
  Alert,
  AlertTitle,
  Autocomplete,
  Box,
  Button,
  Card,
  CardContent,
  CircularProgress,
  Snackbar,
  TextField,
  Typography,
} from '@mui/material';
import { useCallback, useEffect, useState } from 'react';
import { Title, useRedirect } from 'react-admin';
import { getClinicsForSelect } from '../../lib/clinic';
import MixpanelService from '../../services/mixpanel';
import referralService from '../../services/referral';
import {
  isStructuredReferralUpload,
  REFERRAL_UPLOAD_ACCEPT,
  REFERRAL_UPLOAD_HINT,
  resolveReferralUploadType,
} from '../../utils/referralUpload.utils';
import { PageHeader } from '../common/PageHeader/PageHeader';
import '../common/tab-section.css';

interface Clinic {
  id: string;
  name: string;
}

const DocumentUploader: React.FC = () => {
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [processingDocument, setProcessingDocument] = useState<boolean>(false);
  const [snackbarText, setSnackbarText] = useState<string | null>(null);
  const [clinics, setClinics] = useState<Clinic[]>([]);
  const [selectedClinic, setSelectedClinic] = useState<string>('');
  const [dragActive, setDragActive] = useState(false);

  const redirect = useRedirect();

  // A referral package is read structurally, and nothing in it is matched to one of our
  // clinics — that matching is the LLM's, on the PDF path. Uploaded without a clinic, a
  // package became a referral with no clinic, and with no clinic its cohort (Maintain for
  // OMC, set per clinic) could not resolve either. So for a package the clinic is required.
  const isPackage = !!selectedFile && isStructuredReferralUpload(selectedFile);
  const clinicMissing = isPackage && !selectedClinic;

  const handleUploadDocument = async (): Promise<void> => {
    if (!selectedFile) {
      setSnackbarText('Please select a file to upload.');
      return;
    }

    if (clinicMissing) {
      setSnackbarText('Choose the receiving clinic for a referral package.');
      return;
    }

    // Decided from the extension, not from `file.type`, and then used for both the presigned
    // URL and the PUT header so the two cannot disagree.
    const contentType = resolveReferralUploadType(selectedFile);

    if (!contentType) {
      setSnackbarText(`Unsupported file. Upload a ${REFERRAL_UPLOAD_HINT}.`);
      return;
    }

    setProcessingDocument(true);

    try {
      const uploadUrlResult = await referralService.createUploadUrl(
        selectedFile.name,
        contentType,
      );

      if (!uploadUrlResult) {
        setSnackbarText('Could not create upload URL.');
        return;
      }

      const parsingResult = await referralService.pushContentAndParseReferral(
        selectedClinic || ' ', // Allow empty clinic for LLM detection
        uploadUrlResult.uploadUrl,
        uploadUrlResult.destination,
        selectedFile,
        contentType,
      );

      if (!parsingResult) {
        setSnackbarText(
          'An error occurred while trying to upload the document and parse its content.',
        );
        return;
      }

      // The file's *name* is deliberately not tracked. Referral documents are routinely named
      // after the patient by the sending system, which would put PHI into Mixpanel. The type
      // and size are what the event is actually useful for.
      MixpanelService.trackButtonClicked({
        Field: 'Upload Document',
        source: '/documents/upload',
        document: {
          fileType: contentType,
          fileSizeKb: Math.round(selectedFile.size / 1024),
          clinic: selectedClinic,
        },
      });

      setSnackbarText(
        `Document queued successfully! Processing will begin shortly and you'll be notified when complete.`,
      );

      // Navigate back to documents list after success
      setTimeout(() => {
        redirect('/documents/library');
      }, 3000);
    } catch (error) {
      setSnackbarText('An error occurred while uploading the document.');
    } finally {
      setProcessingDocument(false);
    }
  };

  const handleDrag = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === 'dragenter' || e.type === 'dragover') {
      setDragActive(true);
    } else if (e.type === 'dragleave') {
      setDragActive(false);
    }
  }, []);

  const handleDrop = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);

    if (e.dataTransfer.files && e.dataTransfer.files[0]) {
      const file = e.dataTransfer.files[0];
      if (resolveReferralUploadType(file)) {
        setSelectedFile(file);
      } else {
        setSnackbarText(`Unsupported file. Upload a ${REFERRAL_UPLOAD_HINT}.`);
      }
    }
  }, []);

  const handleFileInput = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files[0]) {
      const file = e.target.files[0];
      if (resolveReferralUploadType(file)) {
        setSelectedFile(file);
      } else {
        setSnackbarText(`Unsupported file. Upload a ${REFERRAL_UPLOAD_HINT}.`);
      }
    }
  };

  const handleFileAreaClick = () => {
    const fileInput = document.getElementById('file-input') as HTMLInputElement;
    if (fileInput) {
      fileInput.click();
    }
  };

  useEffect(() => {
    const getClinics = async () => {
      try {
        const clinicsAllowed = await getClinicsForSelect();
        const clinicsForSelect = clinicsAllowed.map((clinic) => ({
          id: clinic.slug,
          name: clinic.name,
        }));
        setClinics(clinicsForSelect);
      } catch (error) {
        console.error('Error fetching clinics:', error);
      }
    };
    getClinics();
  }, []);

  return (
    <Box>
      <Title title='Upload Document' />

      <Box className='tab-section'>
        <PageHeader
          breadcrumbs={[
            { label: 'Documents', onClick: () => redirect('/documents/library') },
            { label: 'Upload Document', active: true },
          ]}
        />

        <Typography variant='h6' sx={{ mb: 3 }}>
          Upload Document
        </Typography>

        <Alert severity='info' sx={{ mb: 3 }}>
          <AlertTitle>Processing Information</AlertTitle>
          <Box component='ul' sx={{ m: 0, pl: 2 }}>
            <li>Only one document can be uploaded at a time</li>
            <li>Data extraction is currently enabled for referral-order documents only</li>
            <li>
              Documents are processed asynchronously - you'll receive updates on completion status
            </li>
            <li>Processing typically takes 2-5 minutes depending on document complexity</li>
          </Box>
        </Alert>

        <Card>
          <CardContent sx={{ p: 3 }}>
            <Autocomplete
              options={clinics}
              getOptionLabel={(option) => option.name}
              value={selectedClinic ? clinics.find((c) => c.id === selectedClinic) || null : null}
              onChange={(_, newValue) => {
                setSelectedClinic(newValue?.id || '');
              }}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label={isPackage ? 'Clinic' : 'Clinic (Optional)'}
                  required={isPackage}
                  error={clinicMissing}
                  placeholder='Search or select a clinic...'
                  size='small'
                  helperText={
                    isPackage
                      ? 'Required for a referral package: it is not matched to a clinic automatically, and the clinic decides its cohort'
                      : 'Leave empty to let the system automatically detect the clinic from the document'
                  }
                />
              )}
              isOptionEqualToValue={(option, value) => option.id === value.id}
              sx={{ mb: 3 }}
              size='small'
              autoSelect={false}
              autoHighlight={false}
            />

            <Box
              sx={{
                border: 2,
                borderColor: dragActive ? 'primary.main' : 'grey.300',
                borderStyle: 'dashed',
                borderRadius: 2,
                p: 4,
                textAlign: 'center',
                mb: 3,
                backgroundColor: dragActive ? 'action.hover' : 'background.paper',
                cursor: 'pointer',
                transition: 'all 0.2s ease-in-out',
                '&:hover': {
                  borderColor: 'primary.main',
                  backgroundColor: 'action.hover',
                },
              }}
              onDragEnter={handleDrag}
              onDragLeave={handleDrag}
              onDragOver={handleDrag}
              onDrop={handleDrop}
              onClick={handleFileAreaClick}>
              <input
                id='file-input'
                type='file'
                accept={REFERRAL_UPLOAD_ACCEPT}
                style={{ display: 'none' }}
                onChange={handleFileInput}
              />

              <FileUploadIcon
                sx={{
                  fontSize: 48,
                  color: dragActive ? 'primary.main' : 'grey.400',
                  mb: 2,
                }}
              />

              {selectedFile ? (
                <Box>
                  <Typography variant='h6' color='success.main'>
                    {selectedFile.name}
                  </Typography>
                  <Typography variant='body2' color='text.secondary'>
                    {(selectedFile.size / 1024 / 1024).toFixed(2)} MB
                  </Typography>
                  {isStructuredReferralUpload(selectedFile) && (
                    <Typography
                      variant='caption'
                      color='primary.main'
                      display='block'
                      sx={{ mt: 1 }}>
                      Referral package — fields are read directly from the document, so there
                      is no page preview
                    </Typography>
                  )}
                  <Typography
                    variant='caption'
                    color='text.secondary'
                    display='block'
                    sx={{ mt: 1 }}>
                    Click or drag to replace file
                  </Typography>
                </Box>
              ) : (
                <Box>
                  <Typography variant='h6' gutterBottom>
                    Drop your referral document here
                  </Typography>
                  <Typography variant='body2' color='text.secondary'>
                    or click to browse files
                  </Typography>
                  <Typography
                    variant='caption'
                    color='text.secondary'
                    display='block'
                    sx={{ mt: 1 }}>
                    {REFERRAL_UPLOAD_HINT}
                  </Typography>
                </Box>
              )}
            </Box>

            <Box sx={{ display: 'flex', gap: 2, justifyContent: 'flex-end' }}>
              <Button
                variant='outlined'
                onClick={() => {
                  redirect('/documents/library');
                }}
                disabled={processingDocument}>
                Cancel
              </Button>
              <Button
                variant='contained'
                className='primary-button'
                onClick={handleUploadDocument}
                startIcon={
                  processingDocument ? (
                    <CircularProgress size={16} color='inherit' />
                  ) : (
                    <FileUploadIcon />
                  )
                }
                disabled={!selectedFile || clinicMissing || processingDocument}>
                {processingDocument ? 'Queuing Document...' : 'Queue for Processing'}
              </Button>
            </Box>
          </CardContent>
        </Card>
      </Box>

      <Snackbar
        open={!!snackbarText}
        autoHideDuration={8000}
        onClose={() => {
          setSnackbarText(null);
        }}
        message={snackbarText}
      />
    </Box>
  );
};

export default DocumentUploader;
